import { RefObject, useEffect, useRef, useState } from 'react';

type UseStickyHeaderOptions = {
  /** Threshold below which the header becomes sticky (default: 10) */
  stickyThreshold?: number;
  /** Threshold above which the header becomes non-sticky (default: 30) */
  unstickyThreshold?: number;
};

type UseStickyHeaderResult = {
  /** Ref to attach to the sentinel element that triggers sticky detection */
  sentinelRef: RefObject<HTMLDivElement>;
  /** Whether the header is currently in sticky state */
  isSticky: boolean;
};

/**
 * Hook for detecting when a header should become sticky based on scroll position.
 *
 * Uses a sentinel element to detect when the header area has scrolled out of view.
 * Implements hysteresis (dead zone) to prevent flickering when scrolling near the threshold.
 *
 * @param enabled - Whether sticky header detection should be active
 * @param options - Configuration options for thresholds
 * @returns Object containing sentinelRef to attach to a div and isSticky state
 *
 * @example
 * ```tsx
 * function MyComponent({ isStickyHeader }: { isStickyHeader?: boolean }) {
 *   const { sentinelRef, isSticky } = useStickyHeader(isStickyHeader);
 *
 *   return (
 *     <div>
 *       {isStickyHeader && <div ref={sentinelRef} className={styles.sentinel} />}
 *       <header className={clsx(styles.header, isSticky && styles.headerSticky)}>
 *         ...
 *       </header>
 *     </div>
 *   );
 * }
 * ```
 */
export function useStickyHeader(
  enabled: boolean = false,
  options: UseStickyHeaderOptions = {}
): UseStickyHeaderResult {
  const { stickyThreshold = 10, unstickyThreshold = 30 } = options;

  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enabled || !sentinelRef.current) return;

    // Find the scrollable parent container (the one with overflow: auto/scroll)
    const findScrollParent = (element: HTMLElement | null): HTMLElement | null => {
      if (!element || element === document.body) return null;
      const overflow = getComputedStyle(element).overflowY;
      if (overflow === 'auto' || overflow === 'scroll') {
        return element;
      }
      return findScrollParent(element.parentElement);
    };

    const scrollContainer = findScrollParent(sentinelRef.current);
    if (!scrollContainer) return;

    const checkStickyState = () => {
      if (!sentinelRef.current) return;

      // If scrolled to the very top, always revert to non-sticky
      if (scrollContainer.scrollTop === 0) {
        setIsSticky(false);
        return;
      }

      const containerRect = scrollContainer.getBoundingClientRect();
      const sentinelRect = sentinelRef.current.getBoundingClientRect();
      // Position of sentinel relative to scroll container top
      const relativeTop = sentinelRect.top - containerRect.top;

      // Use hysteresis to prevent oscillation
      if (relativeTop < stickyThreshold) {
        setIsSticky(true);
      } else if (relativeTop > unstickyThreshold) {
        setIsSticky(false);
      }
      // Between thresholds: maintain current state (dead zone)
    };

    scrollContainer.addEventListener('scroll', checkStickyState, { passive: true });

    return () => scrollContainer.removeEventListener('scroll', checkStickyState);
  }, [enabled, stickyThreshold, unstickyThreshold]);

  return { sentinelRef, isSticky };
}
